import { Link, useLocation, useNavigate } from "react-router-dom";
import { MessageSquareText, FileText, User as UserIcon, LogOut, ArrowLeft, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";

const NAV_ITEMS = [
  { to: "/chat", label: "Chat", icon: Sparkles },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/profile", label: "Profile", icon: UserIcon },
];

export default function AppShell({ children, title, eyebrow, action }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const initials = (user?.name || user?.email || "U").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div data-testid="app-shell" className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-[260px] flex-col border-r border-border bg-card">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <Logo />
          <ThemeToggle />
        </div>
        <nav className="p-3 flex-1">
          <Button asChild className="w-full justify-start rounded-xl h-10 bg-primary hover:bg-primary/90 text-primary-foreground mb-3">
            <Link to="/chat" data-testid="shell-new-chat-btn"><MessageSquareText className="w-4 h-4 mr-2" /> Start new chat</Link>
          </Button>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground px-1 mb-2">Workspace</p>
          <div className="space-y-1">
            {NAV_ITEMS.map((it) => {
              const active = location.pathname === it.to || (it.to === "/chat" && location.pathname.startsWith("/chat"));
              return (
                <Link
                  key={it.to}
                  to={it.to}
                  data-testid={`shell-nav-${it.label.toLowerCase()}`}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                    active ? "bg-accent/10 text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <it.icon className={`w-4 h-4 ${active ? "text-accent" : ""}`} /> {it.label}
                </Link>
              );
            })}
          </div>
        </nav>
        <div className="p-3 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="shell-user-menu"
                className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted transition-colors"
              >
                <Avatar className="w-8 h-8">
                  {user?.picture ? <AvatarImage src={user.picture} alt={user.name} /> : null}
                  <AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0 text-left">
                  <div className="text-sm font-medium truncate">{user?.name || user?.email}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs text-muted-foreground">Account</DropdownMenuLabel>
              <DropdownMenuItem asChild><Link to="/">← Back to landing</Link></DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="shell-logout-btn"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-destructive focus:text-destructive"
              >
                <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <div className="h-14 border-b border-border bg-background/80 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <Link to="/chat" className="md:hidden"><Button variant="ghost" size="sm" className="rounded-full"><ArrowLeft className="w-4 h-4" /></Button></Link>
            <div>
              {eyebrow && <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-accent leading-none mb-0.5">{eyebrow}</p>}
              <h1 className="font-display text-base font-semibold tracking-tight leading-none">{title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">{action}</div>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </main>
    </div>
  );
}
